import * as zod from 'zod';

/**
 * Shared contract for the echo-bot configuration panel.
 *
 * `config` is **snake_case**, 1:1 with `echo-bot-config.yaml` under the
 * Dynaconf `default:` envelope — no camelCase remap, so dump/load round-trips
 * cleanly. Keys are classified into three tiers:
 *
 *  1. Surfaced  — strictly validated, rendered as form fields (the panel).
 *  2. Hidden    — live v1/infra/demo keys, loosely validated + passed through
 *                 on write, never flagged as unknown, not rendered.
 *  3. Unknown   — anything else in the file → counted in staleness.unknownKeys.
 */

/** Tier 1: surfaced, strictly-validated keys (rendered as form fields). */
export const EchoBotConfigSurfacedSchema = zod.object({
  endpoint: zod.string().url(),
  upload: zod.boolean(),
  cloud_processing: zod.boolean(),
  upload_max_concurrent_files: zod.number().int().positive(),
  upload_max_bandwidth_mbps: zod.number().positive(),
  data_processor_job: zod.object({
    image_url: zod.string().min(1),
    service_account_key_bucket: zod.string(),
    service_account_key_file: zod.string(),
  }),
  handheld_imports: zod.object({
    // project_id/project_name scope the task & user dropdowns in the panel.
    project_id: zod.string().optional(),
    project_name: zod.string().optional(),
    // The single task/user every handheld .insv import is attributed to
    // (selected via the panel dropdowns). echo-bot uses these directly,
    // independent of the camera model.
    task_id: zod.string().min(1),
    user_id: zod.string().optional(),
    // When true, upload .insv recordings even if api-srv reports the same raw
    // recording was already uploaded (bypasses duplicate rejection). Optional:
    // absent means "don't force" (the panel reads it as `?? false`), so a
    // partial patch needn't carry it.
    force_upload: zod.boolean().optional(),
  }),
  apriltag_priors: zod.object({
    workstations: zod.array(
      zod.object({
        id: zod.string().min(1),
        active: zod.boolean().optional(),
        tag_family: zod.string(),
        tag_size_m: zod.number().positive(),
        tags: zod.array(
          zod.object({
            id: zod.number().int(),
            position_home_relative_m: zod.tuple([
              zod.number(),
              zod.number(),
              zod.number(),
            ]),
          }),
        ),
      }),
    ),
  }),
  handheld_camera: zod.object({ t_flange_cam_yaml: zod.string() }),
});

/**
 * Tier 2: known-but-hidden keys. Live (v1 recording / infra / demo) but not
 * surfaced. Loosely typed, never flagged as unknown, passed through on write.
 */
export const ECHO_BOT_HIDDEN_KEYS = [
  'host',
  'port',
  'gripper_joint_numbers',
  'gripper_zero_is_closed',
  'recording_dir',
  'bot_identity_config_path',
  'tcp_offset',
  'robot_name',
  'use_ee_board',
  'use_default_recorder_config',
  'default_config',
] as const;

/** Full config = surfaced (strict) + hidden (passthrough) + unknown (kept). */
export const EchoBotConfigSchema = EchoBotConfigSurfacedSchema.extend({
  host: zod.string().optional(),
  port: zod.number().optional(),
  gripper_joint_numbers: zod.array(zod.array(zod.number())).optional(),
  gripper_zero_is_closed: zod.boolean().optional(),
  recording_dir: zod.string().optional(),
  bot_identity_config_path: zod.string().optional(),
  tcp_offset: zod.array(zod.number()).optional(),
  robot_name: zod.string().optional(),
  use_ee_board: zod.boolean().optional(),
  use_default_recorder_config: zod.boolean().optional(),
  default_config: zod
    .object({ user_id: zod.string(), task_id: zod.string() })
    .optional(),
}).passthrough();

export type EchoBotConfig = zod.infer<typeof EchoBotConfigSchema>;

/** Keys echo-bot expects (surfaced + hidden); used for staleness. */
export const ECHO_BOT_KNOWN_KEYS: string[] = [
  ...Object.keys(EchoBotConfigSurfacedSchema.shape),
  ...ECHO_BOT_HIDDEN_KEYS,
];

export interface EchoBotConfigState {
  /** Did the `/etc` override file exist, or are we showing bundled defaults? */
  source: 'override-file' | 'defaults';
  staleness: { missingKeys: string[]; unknownKeys: string[] };
  /** snake_case, mirrors the yaml under `default:`. */
  config: EchoBotConfig;
}

/**
 * Pure helper — shared by the feathers service and tests.
 *
 * `missingKeys` = known keys (tiers 1+2) absent from the file → e.g. a stale
 * file missing `handheld_imports`. `unknownKeys` = keys in the file that aren't
 * known → dead cruft like `vicon_host`.
 */
export function computeStaleness(fileKeys: string[]): {
  missingKeys: string[];
  unknownKeys: string[];
} {
  const known = new Set(ECHO_BOT_KNOWN_KEYS);
  const present = new Set(fileKeys);

  return {
    missingKeys: ECHO_BOT_KNOWN_KEYS.filter((k) => !present.has(k)),
    unknownKeys: fileKeys.filter((k) => !known.has(k)),
  };
}
